import React, { createContext, useState, useContext, useEffect, ReactNode } from "react";
import useLocalStorage from "@/hooks/useLocalStorage";
import { findUser, findUserNotificationSetting } from "@/services/UserService";
import uuid4 from "uuid4";
import { isNull } from "lodash";
import { User, UserContextType, UUID, ID } from "@/types";
import { logger } from "@/services/LoggerService";

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
    const initialUser: User = {
        id: "",
        first_name: "",
        last_name: "",
        emailAddress: "",
        nickname: "",
        isPro: false,
        showStartupModal: true,
        showScoreLeaderboard: true,
        notifyDailyProperty: true,
        notification_settings: [],
        createdAt: "",
        updatedAt: "",
        deletedAt: null,
        is_created: false,
        user_retrieve_error: false,
        currentAreaId: "",
        currentLocation: "",
        show_scrollability_tooltip: false,
        useEmailAccountPicture: true,
        challengeHistorySelectedUser: { id: null, nickname: "" },
        leaderboard_active_tab: { tab: 0, button: 0 },
        guest_challenge_redirect: "other",
        challenge_register_button: "NA",
        guest_final_score_modal_version: "NA",
        leaderboardSelectedVersion: "puzzle",
    };

    const [dbUser, setDbUser, clearDbUser] = useLocalStorage<User>("user_data", initialUser);

    const [userState, setUser] = useState<User>(dbUser);
    const [isInvalidLogin, setInvalidLogin] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    let hasFetchedData = false;

    /**
     * Updates the user state and sets the 'is_created' property to indicate whether the user was newly created or not.
     *
     * @param {Object} user - The user object to be updated.
     * @param {boolean} isUserCreated - Indicates if the user was newly created (true) or not (false).
     * @param {Object} otherProps - Other user values to be updated.
     */
    const setUserState = (user: User | null, isUserCreated?: boolean, otherProps: Partial<User> = {}) => {
        // We will anticipate a null user object for guest users and generate a random uuid4 value to its user id
        if (isNull(user)) {
            user = { ...initialUser, id: uuid4() };
        }

        /**
         * The updated user data object with the 'is_created' property.
         * @type {Object}
         * @property {string} id - The user's ID.
         * @property {string} name - The user's name.
         * @property {string} email - The user's email address.
         * @property {boolean} is_created - Indicates if the user was newly created.
         * @property {...} otherProperties - Other properties of the user object.
         */
        const userData = { ...user, is_created: !!isUserCreated, ...otherProps } as User;

        // Call the setDbUser function to update the user object in the state or context
        setDbUser(userData);
    };

    /**
     * Clears the user state by resetting it to the initial user state and clearing any stored user data from the database.
     *
     * @param {Object} initialUser - The initial user state to reset the user data to.
     */
    const clearUserState = () => {
        setUser(initialUser);
        clearDbUser();
    };

    /**
     * Fetches user data from the API
     *
     * @param {string} userId - The ID of the user to fetch data for.
     */
    const fetchUserData = async (userId: ID) => {
        if (!hasFetchedData) {
            try {
                setIsLoading(true);
                hasFetchedData = true;

                const fetchedUser = await findUser(String(userId));
                const fetchedUserNotifSetting = await findUserNotificationSetting(String(userId));

                // Update the local storage with the retrieved user data
                // Preserve leaderboard_active_tab from existing state to prevent resetting the daily/weekly toggle
                setDbUser({
                    ...(dbUser ?? {}),
                    ...fetchedUser,
                    notification_settings: Array.isArray(fetchedUserNotifSetting) ? fetchedUserNotifSetting : [],
                    leaderboard_active_tab: dbUser?.leaderboard_active_tab ?? fetchedUser?.leaderboard_active_tab ?? { tab: 0, button: 0 },
                });
            } catch (error) {
                hasFetchedData = false;
                // Handle error if the API call fails
                console.error("Error fetching user data:", error);
                logger.error("Error fetching user data", error, {
                    userId: String(userId),
                    context: "UserContext",
                });
                setInvalidLogin(true);
            } finally {
                setIsLoading(false);
            }
        }
    };

    /**
     * Updates guest user current area id
     *
     * @param {string} areaId - The selected area id.
     */
    const updateGuestCurrentArea = (areaId: UUID) => {
        if (dbUser) {
            setDbUser({ ...dbUser, currentAreaId: areaId });
        }
    };

    useEffect(() => {
        setUser(dbUser);
    }, [dbUser]);

    return (
        <UserContext.Provider
            value={{
                userState,
                isInvalidLogin,
                hasFetchedData,
                isLoading,
                setUserState,
                clearUserState,
                setInvalidLogin,
                fetchUserData,
                updateGuestCurrentArea,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export const useUser = (): UserContextType => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error("useUser must be used within a UserProvider");
    }
    return context;
};
